import React from 'react';
import { useFormContext } from 'react-hook-form';

import { Box, Grid, InputAdornment } from '@mui/material';
import { InputLabelBase } from '../../inputs/InputLabelBase';
import { SimpleSelect } from '../../inputs/selects/SimpleSelect';
import { TextFieldBase } from '../../inputs/TextFieldBase';
import { PhoneField as PhoneFieldBase } from '../../inputs/PhoneField';
import { DomainField as DomainFieldBase } from '../../inputs/DomainField';
import { NaicsCodeField } from '../../inputs/NaicsCodeField';
import { withFormController } from '../../hocs/forms';

// utils
import { yesNoOptions } from '../../../utils';
import { UsStates } from '../../../utils/USState';
import LanguageCurrencyFieldBase from '../../inputs/LanguageCurrencyField';
import usePlatformRegion from '../../../utils/hooks/usePlatformRegion';

// components

const autoCompleteLabelProps = {
  style: { paddingTop: 0 },
  indent: false,
};

export const inputProps = {
  style: {
    border: 'none',
  },
};

const entityTypeOptions = [
  { value: 'Independent', label: 'Independent' },
  { value: 'Holding', label: 'Holding' },
  { value: 'Subsidiary', label: 'Subsidiary' },
  { value: 'Branch', label: 'Branch' },
  { value: 'Parent', label: 'Parent' },
];

const ownershipTypeOptions = [
  { value: 'Public', label: 'Public' },
  { value: 'Private', label: 'Private' },
  { value: 'Non-Profit', label: 'Non-Profit' },
  { value: 'Public Sector', label: 'Public Sector' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Non-Corporates', label: 'Non-Corporates' },
];

const LanguageCurrencyField = withFormController(LanguageCurrencyFieldBase);
const Select = withFormController(SimpleSelect);
const PhoneField = withFormController(PhoneFieldBase);
const TextField = withFormController(TextFieldBase);

export const AccountDetailsFields = ({ disabled }: any) => {
  const methods = useFormContext();
  const { isAU } = usePlatformRegion();

  const { primaryNaicsPercentage, ...values } = methods.getValues();

  const handlePrimaryNaics = (naicsData: any) => {
    methods.setValue('naicsCode', naicsData.value);
    methods.setValue('primaryIndustry', naicsData?.meta?.primaryIndustry);
    methods.setValue('secondaryIndustry', naicsData?.meta?.secondaryIndustry);
  };

  const handleSecondaryNaics = (naicsData: any) => {
    methods.setValue('secondNaicsCode', naicsData?.value ?? undefined);
    methods.setValue(
      'secondPrimaryIndustry',
      naicsData?.meta?.primaryIndustry ?? undefined
    );
    methods.setValue(
      'secondSecondaryIndustry',
      naicsData?.meta?.secondaryIndustry ?? undefined
    );
  };

  return (
    <Grid container spacing={8} style={{ padding: '0 2rem' }}>
        <Grid item md={6}>
          <Box>
            <InputLabelBase required>Name</InputLabelBase>
            <TextField
              name="name"
              inputProps={inputProps}
              fullWidth
              placeholder="Company name"
              disabled={disabled}
            />
          </Box>
          <Box>
            <InputLabelBase>DBA</InputLabelBase>
            <TextField
              name="dbaOrTradestyle"
              inputProps={inputProps}
              fullWidth
              placeholder="DBA"
              disabled={disabled}
            />
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom" required>
              Primary Industry Code
            </InputLabelBase>
            <Box display="flex" justifyContent="space-between">
              <NaicsCodeField
                name="naicsCode"
                labelProps={autoCompleteLabelProps}
                placeholder="Primary Industry code"
                defaultValue={values.naicsCode}
                required
                textFieldProps={inputProps}
                disabled={disabled}
                onChange={handlePrimaryNaics}
              />
              <Box flexGrow="1" ml={1}>
                <TextField
                  name="primaryNaicsPercentage"
                  defaultValue={primaryNaicsPercentage}
                  inputProps={{
                    min: 51,
                    max: 100,
                    ...inputProps,
                  }}
                  type="number"
                  placeholder="75"
                  disabled={disabled}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box>
            <InputLabelBase
              className="classes.inputLabelCustom"
              required={primaryNaicsPercentage != 100}
            >
              Secondary Industry Code
            </InputLabelBase>
            <Box display="flex" justifyContent="space-between">
              <NaicsCodeField
                name="secondNaicsCode"
                labelProps={autoCompleteLabelProps}
                placeholder="Secondary Industry code"
                defaultValue={values.secondNaicsCode}
                readOnly={false}
                textFieldProps={inputProps}
                disabled={disabled}
                disableClearable={false}
                onChange={handleSecondaryNaics}
              />
              <Box flexGrow="1" ml={1}>
                <TextField
                  name="secondaryNaicsPercentage"
                  defaultValue={100 - Number(primaryNaicsPercentage)}
                  inputProps={{
                    min: 0,
                    max: 49,
                    ...inputProps,
                  }}
                  type="number"
                  placeholder="25"
                  disabled
                  fullWidth
                  value={100 - Number(primaryNaicsPercentage)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom" required>
              Projected Revenue
            </InputLabelBase>
            <Box>
              <LanguageCurrencyField
                name="revenue"
                defaultValue={values.revenue}
                inputProps={inputProps}
                fullWidth
                disabled={disabled}
              />
            </Box>
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom">
              DUNS
            </InputLabelBase>
            <Box>
              <TextField
                name="dunsNumber"
                defaultValue={values.dunsNumber}
                inputProps={inputProps}
                type="number"
                fullWidth
                placeholder="1234567"
                disabled={disabled}
              />
            </Box>
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom" required>
              Number of Employees
            </InputLabelBase>
            <TextField
              name="noOfEmployeesAll"
              defaultValue={values.noOfEmployeesAll}
              inputProps={inputProps}
              type="number"
              fullWidth
              placeholder="132"
              disabled={disabled}
            />
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom" required>
              Year Established
            </InputLabelBase>
            <Box>
              <TextField
                name="yearEstablished"
                defaultValue={values.yearEstablished}
                inputProps={{ ...inputProps, maxLength: 4 }}
                fullWidth
                placeholder="1985"
                disabled={disabled}
              />
            </Box>
          </Box>
          <Box>
            <Select
              name="entityType"
              label="Entity Type"
              controllerProps={{ control: methods.control }}
              defaultValue={values.entityType}
              options={entityTypeOptions}
              fullWidth
              variant="contained"
              disabled={disabled}
              required
              InputLabelProps={{ indent: false }}
              ErrorProps={{ renderErrorPlaceholder: true }}
            />
          </Box>
          <Box>
            <Select
              name="ownershipType"
              label="Ownership Type"
              controllerProps={{ control: methods.control }}
              defaultValue={values.ownershipType}
              options={ownershipTypeOptions}
              fullWidth
              variant="contained"
              disabled={disabled}
              required
              InputLabelProps={{ indent: false }}
              ErrorProps={{ renderErrorPlaceholder: true }}
            />
          </Box>
        </Grid>
        <Grid item md={6}>
          <Box>
            <Select
              name="isFranchise"
              label="Subsidiary/Franchisee"
              required
              defaultValue={values.isFranchise === 'Yes' ? 'Yes' : 'No'}
              options={yesNoOptions}
              fullWidth
              variant="contained"
              disabled={disabled}
              InputLabelProps={{ indent: false }}
              ErrorProps={{ renderErrorPlaceholder: true }}
            />
          </Box>
          <Box>
            <InputLabelBase>Primary Web Domain</InputLabelBase>
            <DomainField
              name="domainName"
              defaultValue={values.domainName}
              inputProps={inputProps}
              fullWidth
              placeholder="domain.com"
              disabled={disabled}
              ErrorProps={{ renderErrorPlaceholder: true }}
            />
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom">
              Secondary Web Domain(s)
            </InputLabelBase>
            <DomainField
              name="domains"
              defaultValue={values.allOtherDomains}
              inputProps={inputProps}
              fullWidth
              placeholder="domain.com, domain.net"
              disabled={disabled}
              ErrorProps={{ renderErrorPlaceholder: true }}
            />
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom">
              URL
            </InputLabelBase>
            <DomainField
              name="url"
              defaultValue={values.url}
              inputProps={inputProps}
              fullWidth
              disabled={disabled}
              ErrorProps={{ renderErrorPlaceholder: true }}
            />
          </Box>
          {!isAU && (
            <Box>
              <InputLabelBase className="classes.inputLabelCustom">
                Number of PII / PHI / PCI Records
              </InputLabelBase>
              <TextField
                name="numberOfPersonalRecords"
                defaultValue={values.numberOfPersonalRecords}
                inputProps={inputProps}
                type="number"
                fullWidth
                disabled={disabled}
              />
            </Box>
          )}
          <Box>
            <InputLabelBase className="classes.inputLabelCustom">
              % of International Sales
            </InputLabelBase>
            <TextField
              name="percentInternationalSales"
              defaultValue={values.percentInternationalSales}
              inputProps={inputProps}
              type="number"
              fullWidth
              disabled={disabled}
            />
          </Box>
          <Box alignItems="flex-start">
            <Box>
              <Box>
                <InputLabelBase className="classes.inputLabelCustom" required>
                  Address 1
                </InputLabelBase>
                <TextField
                  name="address1"
                  defaultValue={values.address1}
                  inputProps={inputProps}
                  fullWidth
                  placeholder="123 Main St."
                  disabled={disabled}
                />
              </Box>
              <Box>
                <InputLabelBase className="classes.inputLabelCustom">
                  Address 2
                </InputLabelBase>
                <TextField
                  name="address2"
                  defaultValue={values.address2}
                  inputProps={inputProps}
                  fullWidth
                  placeholder="Suite #123"
                  disabled={disabled}
                />
              </Box>
              <Box display="flex" paddingTop="4px">
                <Box flexGrow="1">
                  <InputLabelBase className="classes.inputLabelCustom" required>
                    City
                  </InputLabelBase>
                  <TextField
                    name="city"
                    defaultValue={values.city}
                    inputProps={inputProps}
                    placeholder="San Marco"
                    disabled={disabled}
                  />
                </Box>
                <Box marginRight="5px" marginLeft="5px">
                  <InputLabelBase className="classes.inputLabelCustom" required>
                    State
                  </InputLabelBase>
                  <Select
                    name="state"
                    controllerProps={{ control: methods.control }}
                    defaultValue={values.state}
                    options={UsStates}
                    fullWidth
                    fullHeight
                    variant="contained"
                    disabled={disabled}
                  />
                </Box>
                <Box maxWidth="30%">
                  <InputLabelBase className="classes.inputLabelCustom" required>
                    Zip Code
                  </InputLabelBase>
                  <TextField
                    name="zipCode"
                    defaultValue={values.zipCode}
                    inputProps={inputProps}
                    placeholder="92024"
                    disabled={disabled}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box>
            <InputLabelBase className="classes.inputLabelCustom" required>
              Business Phone Number
            </InputLabelBase>
            <PhoneField
              name="phoneNumber"
              defaultValue={values.phoneNumber}
              inputProps={inputProps}
              fullWidth
              placeholder="(123) 456-7890"
              disabled={disabled}
            />
          </Box>
        </Grid>
      </Grid>
  );
};

const DomainField = (props: any) => {
  const { setValue } = useFormContext();

  function handleInputChange(originalValue: any, cleanedValue: any) {
    setValue(props.name, cleanedValue);
  }

  return <DomainFieldBase onInputChange={handleInputChange} {...props} />;
};
